// Config and hooks shared across the named `@github-ui/sanitize` presets.
//
// The link and image hooks are isomorphic: each guards on browser globals so the
// server (linkedom) path is a safe no-op.

import type {Config} from '../types'

// ── Base config shared across the presets ─────────────────────────────────────
// The default-deny posture every preset inherits. `ALLOW_DATA_ATTR: false` denies
// all `data-*` attributes; presets re-permit specific markers via their own
// `ADD_ATTR` / `ADD_URI_SAFE_ATTR` allowlists.
export const SHARED_CONFIG: Readonly<Config> = {
  FORBID_TAGS: ['style'],
  ALLOW_DATA_ATTR: false,
}

// ── Link hardening hook (always on) ───────────────────────────────────────────

// Ensure all sanitized links open in a new window.
// https://github.com/cure53/DOMPurify/tree/main/demos#hook-to-open-all-links-in-a-new-window-link
export const sanitizeNewWindowLinks = (node: Element) => {
  // set all elements owning target to target=_blank
  if ('target' in node || (typeof HTMLAnchorElement !== 'undefined' && node instanceof HTMLAnchorElement)) {
    node.setAttribute('target', '_blank')
    // prevent https://www.owasp.org/index.php/Reverse_Tabnabbing
    node.setAttribute('rel', 'noopener noreferrer')
  }
  // set non-HTML/MathML links to xlink:show=new
  // eslint-disable-next-line github/get-attribute
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    // eslint-disable-next-line github/get-attribute
    node.setAttribute('xlink:show', 'new')
  }
}

// ── Image-domain hardening ────────────────────────────────────────────────────

/**
 * Restrict images to GitHub-controlled domains by removing disallowed `<img>`
 * nodes. Browser-guarded so the server (linkedom) path is a safe no-op.
 *
 * Currently a no-op placeholder.
 */
export const sanitizeNonGitHubImageLinks = (_node: Node) => {
  if (typeof document === 'undefined') return
  // TODO(github/react-platform#2038): restrict <img src> to github-controlled domains.
}
